const PLACEHOLDER_ID_VALUES = new Set([
  "",
  "id",
  "uuid",
  "string",
  "fake",
  "demo",
  "undefined",
  "null",
  "00000000-0000-0000-0000-000000000000"
]);

const ACTOR_FIELD_NAMES = new Set([
  "requesterTeacherId",
  "requesterUserId",
  "requesterName",
  "requesterEmail",
  "actorTeacherId",
  "actorUserId"
]);

export const normaliseWorkflowText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

export const createWorkflowIdempotencyKey = (prefix = "") => {
  const randomPart =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return prefix ? `${prefix}-${randomPart}` : randomPart;
};

export const isPlaceholderWorkflowId = (value) => {
  const normalised = normaliseWorkflowText(value).toLowerCase();

  return (
    PLACEHOLDER_ID_VALUES.has(normalised) ||
    normalised.startsWith("wire-") ||
    normalised.includes("fake") ||
    normalised.includes("demo")
  );
};

const removeEmptyValues = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(removeEmptyValues)
      .filter((item) => item !== undefined && item !== null && item !== "");
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((cleaned, [key, nestedValue]) => {
      if (ACTOR_FIELD_NAMES.has(key)) {
        return cleaned;
      }

      const nextValue = removeEmptyValues(nestedValue);

      if (nextValue !== undefined && nextValue !== null && nextValue !== "") {
        cleaned[key] = nextValue;
      }

      return cleaned;
    }, {});
  }

  return value;
};

export const buildTeacherSupportRequestPayload = ({
  type,
  schoolId,
  target = {},
  details = {},
  source = {}
}) => {
  const payload = removeEmptyValues({
    type,
    schoolId,
    target,
    details,
    source
  });

  return {
    type: payload.type,
    schoolId: payload.schoolId,
    target: payload.target || {},
    details: payload.details || {},
    source: payload.source || {}
  };
};

export const getSupportRequestReference = (response) =>
  response?.reference || response?.data?.reference || response?.requestReference || response?.requestId || "";

export const isDuplicatePendingResponse = (response) =>
  response?.status === "duplicate_pending" ||
  response?.data?.status === "duplicate_pending" ||
  response?.code === "duplicate_pending";

export const getWorkflowResponseStatus = (response) =>
  response?.status || response?.data?.status || "";

export const getWorkflowAuditReference = (response) =>
  response?.auditReference || response?.data?.auditReference || "";

export const isSubmittedRequestResponse = (response) => {
  const status = getWorkflowResponseStatus(response);
  return (status === "submitted" || status === "duplicate_pending") && Boolean(getSupportRequestReference(response));
};

export const assertSubmittedRequestResponse = (response) => {
  if (!isSubmittedRequestResponse(response)) {
    throw new Error("Request response did not include a submitted status and reference.");
  }

  return response;
};

export const isConfirmedMutationResponse = (response, expectedStatuses = []) => {
  const status = getWorkflowResponseStatus(response);
  const statuses = Array.isArray(expectedStatuses) ? expectedStatuses : [expectedStatuses];

  return statuses.includes(status) && Boolean(getWorkflowAuditReference(response));
};

export const assertConfirmedMutationResponse = (response, expectedStatuses = []) => {
  if (!isConfirmedMutationResponse(response, expectedStatuses)) {
    throw new Error("Mutation response did not include the expected status and audit reference.");
  }

  return response;
};

export const getSupportRequestErrorMessage = (error) => {
  const status = error?.response?.status;

  if (status === 400) {
    return "Some required details are missing or invalid. Check the request and try again.";
  }

  if (status === 401 || status === 403) {
    return "You do not have access to send this request.";
  }

  if (status === 404) {
    return "We could not find the selected item. Refresh and choose it again.";
  }

  if (status === 422) {
    return "The request could not be accepted. Check the details and try again.";
  }

  return "We could not send this request. Try again in a moment.";
};

export const hasPlaceholderTargetId = (target = {}) =>
  Object.entries(target).some(
    ([key, value]) =>
      key.toLowerCase().endsWith("id") &&
      value !== undefined &&
      value !== null &&
      isPlaceholderWorkflowId(value)
  );
